import React from "react";
import { ExternalLink, Copy, Check, BookOpen } from "lucide-react";

interface ClickableReportViewerProps {
  content: string;
  className?: string;
  lightMode?: boolean;
}

/**
 * Parses markdown text into React elements with fully interactive, clickable hyperlinks
 * (both markdown [Text](URL) and raw https://... URLs), formatted tables, headings, and lists.
 */
export const ClickableReportViewer: React.FC<ClickableReportViewerProps> = ({
  content,
  className = "",
  lightMode = false
}) => {
  const [copied, setCopied] = React.useState(false);

  if (!content) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Helper to render inline formatting (bold, italic, inline code, and clickable links)
  const renderInlineFormatted = (text: string, keyPrefix: string = "") => {
    const tokenRegex = /(\[([^\]]+)\]\((https?:\/\/[^\s\)]+)\))|(https?:\/\/[^\s\(\)\[\]<>"']+)|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)/g;

    const elements: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let idx = 0;

    while ((match = tokenRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        elements.push(
          <span key={`${keyPrefix}-t-${idx++}`}>
            {text.substring(lastIndex, match.index)}
          </span>
        );
      }

      const [, mdLink, mdLinkText, mdLinkUrl, rawUrl, boldWrap, boldText, italicWrap, italicText, , codeText] = match;

      if (mdLink) {
        // Markdown Link [Text](URL)
        elements.push(
          <a
            key={`${keyPrefix}-l-${idx++}`}
            href={mdLinkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`font-semibold underline decoration-1 underline-offset-2 inline-flex items-center gap-0.5 transition-colors cursor-pointer ${
              lightMode 
                ? "text-blue-600 hover:text-blue-800 decoration-blue-400/60" 
                : "text-sky-400 hover:text-sky-300 decoration-sky-400/50 hover:decoration-sky-300"
            }`}
            title={`Відкрити посилання: ${mdLinkUrl}`}
          >
            <span>{mdLinkText}</span>
            <ExternalLink className="w-3 h-3 inline-block shrink-0 opacity-80" />
          </a>
        );
      } else if (rawUrl) {
        // Raw URL
        elements.push(
          <a
            key={`${keyPrefix}-u-${idx++}`}
            href={rawUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`font-mono text-[11px] underline decoration-1 underline-offset-2 inline-flex items-center gap-0.5 break-all transition-colors cursor-pointer ${
              lightMode 
                ? "text-blue-600 hover:text-blue-800 decoration-blue-400/60" 
                : "text-emerald-400 hover:text-emerald-300 decoration-emerald-400/50"
            }`}
            title={`Відкрити посилання: ${rawUrl}`}
          >
            <span>{rawUrl}</span>
            <ExternalLink className="w-2.5 h-2.5 inline-block shrink-0 opacity-80" />
          </a>
        );
      } else if (boldWrap) {
        // Bold
        elements.push(
          <strong key={`${keyPrefix}-b-${idx++}`} className={lightMode ? "font-bold text-slate-900" : "font-bold text-white"}>
            {boldText}
          </strong>
        );
      } else if (italicWrap) {
        // Italic
        elements.push(
          <em key={`${keyPrefix}-i-${idx++}`} className={lightMode ? "italic text-slate-700" : "italic text-zinc-300"}>
            {italicText}
          </em>
        );
      } else if (codeText) {
        // Inline code
        elements.push(
          <code 
            key={`${keyPrefix}-c-${idx++}`} 
            className={`px-1.5 py-0.5 rounded text-[11px] font-mono ${
              lightMode ? "bg-slate-200 text-slate-800" : "bg-zinc-800 text-amber-300 border border-zinc-700/60"
            }`}
          >
            {codeText}
          </code>
        );
      }

      lastIndex = tokenRegex.lastIndex;
    }

    if (lastIndex < text.length) {
      elements.push(
        <span key={`${keyPrefix}-t-tail`}>
          {text.substring(lastIndex)}
        </span>
      );
    }

    return elements.length > 0 ? elements : text;
  };

  // Block parser
  const lines = content.split("\n");
  const blocks: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Empty line
    if (!trimmed) {
      i++;
      continue;
    }

    // Horizontal Rule
    if (/^(\-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      blocks.push(
        <hr 
          key={`hr-${i}`} 
          className={`my-4 border-0 border-t ${lightMode ? "border-slate-200" : "border-zinc-800"}`} 
        />
      );
      i++;
      continue;
    }

    // Headings
    if (trimmed.startsWith("#")) {
      const match = trimmed.match(/^(#{1,6})\s+(.*)$/);
      if (match) {
        const level = match[1].length;
        const text = match[2];
        const headingClasses = {
          1: `text-lg sm:text-xl font-black mt-5 mb-2 pb-1.5 border-b flex items-center gap-2 ${
            lightMode ? "text-slate-900 border-slate-200" : "text-white border-zinc-800"
          }`,
          2: `text-base sm:text-lg font-bold mt-4 mb-2 flex items-center gap-1.5 ${
            lightMode ? "text-slate-850" : "text-emerald-400"
          }`,
          3: `text-sm sm:text-base font-bold mt-3 mb-1.5 ${
            lightMode ? "text-slate-800" : "text-sky-300"
          }`,
          4: `text-xs sm:text-sm font-semibold mt-2 mb-1 ${
            lightMode ? "text-slate-700" : "text-zinc-200"
          }`,
          5: `text-xs font-semibold mt-2 mb-1 ${lightMode ? "text-slate-700" : "text-zinc-300"}`,
          6: `text-xs font-medium mt-1 mb-1 ${lightMode ? "text-slate-600" : "text-zinc-400"}`
        }[level as 1|2|3|4|5|6] || "text-sm font-bold";

        blocks.push(
          <div key={`h-${i}`} className={headingClasses}>
            {level <= 2 && <BookOpen className="w-4 h-4 shrink-0 opacity-75" />}
            <span>{renderInlineFormatted(text, `h-${i}`)}</span>
          </div>
        );
        i++;
        continue;
      }
    }

    // Markdown Table
    if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("|") && lines[i].trim().endsWith("|")) {
        tableLines.push(lines[i].trim());
        i++;
      }

      if (tableLines.length > 0) {
        const parsedRows = tableLines
          .filter(l => !/^\|[\s:\-\|\=\+]+$/.test(l))
          .map(l => l.slice(1, -1).split("|").map(cell => cell.trim()));

        if (parsedRows.length > 0) {
          const headerRow = parsedRows[0];
          const bodyRows = parsedRows.slice(1);

          blocks.push(
            <div key={`table-${i}`} className="my-3 overflow-x-auto rounded-xl border border-zinc-800 shadow-sm">
              <table className={`w-full text-left text-xs ${lightMode ? "bg-white text-slate-800" : "bg-zinc-950/80 text-zinc-300"}`}>
                <thead className={lightMode ? "bg-slate-100 text-slate-900 border-b border-slate-200 font-bold" : "bg-zinc-900 text-white border-b border-zinc-800 font-bold"}>
                  <tr>
                    {headerRow.map((cell, cIdx) => (
                      <th key={`th-${cIdx}`} className="px-3.5 py-2.5 border-r last:border-r-0 border-zinc-800/60 font-mono text-[11px] uppercase tracking-wider">
                        {renderInlineFormatted(cell, `th-${i}-${cIdx}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/40">
                  {bodyRows.map((row, rIdx) => (
                    <tr key={`tr-${rIdx}`} className={lightMode ? "hover:bg-slate-50" : "hover:bg-zinc-900/50"}>
                      {row.map((cell, cIdx) => (
                        <td key={`td-${rIdx}-${cIdx}`} className="px-3.5 py-2 border-r last:border-r-0 border-zinc-800/30 font-sans text-xs">
                          {renderInlineFormatted(cell, `td-${i}-${rIdx}-${cIdx}`)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          continue;
        }
      }
    }

    // Code Block
    if (trimmed.startsWith("```")) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      if (i < lines.length && lines[i].trim().startsWith("```")) {
        i++;
      }

      blocks.push(
        <pre 
          key={`code-${i}`} 
          className={`my-3 p-3.5 rounded-xl font-mono text-[11px] overflow-x-auto border leading-relaxed ${
            lightMode 
              ? "bg-slate-900 text-slate-100 border-slate-800" 
              : "bg-zinc-950 text-emerald-300 border-zinc-800"
          }`}
        >
          <code>{codeLines.join("\n")}</code>
        </pre>
      );
      continue;
    }

    // Blockquote
    if (trimmed.startsWith(">")) {
      const bqText = trimmed.replace(/^>\s*/, "");
      blocks.push(
        <blockquote 
          key={`bq-${i}`} 
          className={`my-2.5 pl-3.5 py-1.5 border-l-2 rounded-r-lg text-xs italic ${
            lightMode 
              ? "border-blue-500 bg-blue-50/50 text-slate-700" 
              : "border-amber-400 bg-amber-500/5 text-zinc-300"
          }`}
        >
          {renderInlineFormatted(bqText, `bq-${i}`)}
        </blockquote>
      );
      i++;
      continue;
    }

    // Bullet / Ordered List Item
    const listMatch = trimmed.match(/^(\*|-|•|\d+\.)\s+(.*)$/);
    if (listMatch) {
      const marker = listMatch[1];
      const itemText = listMatch[2];
      const isNumber = /^\d+\./.test(marker);

      blocks.push(
        <div key={`li-${i}`} className="flex items-start gap-2 my-1 text-xs leading-relaxed">
          <span className={`shrink-0 font-mono ${
            isNumber 
              ? (lightMode ? "text-blue-600 font-bold" : "text-emerald-400 font-bold") 
              : (lightMode ? "text-slate-400" : "text-zinc-500")
          }`}>
            {marker}
          </span>
          <div className="flex-1">
            {renderInlineFormatted(itemText, `li-${i}`)}
          </div>
        </div>
      );
      i++;
      continue;
    }

    // Regular Paragraph
    blocks.push(
      <p key={`p-${i}`} className="my-1.5 text-xs leading-relaxed">
        {renderInlineFormatted(line, `p-${i}`)}
      </p>
    );
    i++;
  }

  return (
    <div className={`relative font-sans text-xs ${lightMode ? "text-slate-700" : "text-zinc-300"} ${className}`}>
      <div className="flex justify-end mb-2">
        <button
          type="button"
          onClick={handleCopy}
          className={`px-2.5 py-1 rounded-lg text-[11px] font-medium flex items-center gap-1 border transition-all cursor-pointer ${
            lightMode
              ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300"
              : "bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border-zinc-700"
          }`}
          title="Скопіювати весь текст звіту"
        >
          {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          <span>{copied ? "Скопійовано!" : "Копіювати текст"}</span>
        </button>
      </div>

      <div className="space-y-0.5">
        {blocks}
      </div>
    </div>
  );
};
